import { backendClient } from '@/lib/api-client'
import { imanageContainerIdsMatch } from '../imanage/imanageFolderPickerUtils'
import type { SyncMappingMode, SyncPreviewSample } from './syncMapping'
import type { FolderItem } from './WorkspacePicker'

export type IManageV2Auth = {
  accessToken: string
  providerConfig: {
    access_token: string
    dialog_token?: string
    user_id?: string
    customer_id: string
    imanage_account_id: string
  }
}

export type IManageV2Source = FolderItem & {
  database?: string
  workspace_id?: string
  workspace_name?: string
  wstype?: string
  ancestor_ids?: string[]
  preview_children?: SyncPreviewSample[] | null
}

type RunOAuthPopup = <TAuthData>(options: {
  authUrl: string
  popupName: string
  successMessageType: string
  acknowledgmentMessageType: string
  extractAuthData: (eventData: any) => TAuthData | null
  stateStorage?: { key: string; value: string }
}) => Promise<TAuthData>

type IManageAuthPayload = {
  access_token: string
  dialog_token?: string
  user_id?: string
  customer_id: string
  imanage_account_id: string
}

export function mapImanagePickerItems(raw: Array<Record<string, unknown>>): IManageV2Source[] {
  return raw.map((item) => {
    const id = String(item.id || item.wsid || '')
    const wstype = typeof item.wstype === 'string' ? item.wstype : undefined
    return {
      id,
      name: String(item.name || item.database || ''),
      path: String(item.path || `${item.database}/${item.name}`),
      has_children: true,
      document_count: typeof item.document_count === 'number' ? item.document_count : undefined,
      database: typeof item.database === 'string' ? item.database : undefined,
      workspace_id: typeof item.workspace_id === 'string' ? item.workspace_id : undefined,
      workspace_name: typeof item.workspace_name === 'string' ? item.workspace_name : undefined,
      wstype,
      ancestor_ids: wstype === 'workspace' && id
        ? [id]
        : Array.isArray(item.ancestor_ids)
          ? item.ancestor_ids as string[]
          : undefined,
    }
  })
}

function splitContainerId(value: string): { library: string | null; number: string } {
  const text = value.trim().toLowerCase()
  const sep = text.indexOf('!')
  if (sep === -1) {
    return { library: null, number: text }
  }
  return { library: text.slice(0, sep), number: text.slice(sep + 1) }
}

function containerIdsMatch(left: string, right: string): boolean {
  const a = splitContainerId(left)
  const b = splitContainerId(right)
  if (!a.number || !b.number || a.number !== b.number) {
    return false
  }
  if (a.library !== null && b.library !== null) {
    return a.library === b.library
  }
  return true
}

export function mergeResolvedContainers(
  sources: IManageV2Source[],
  containers: Array<Record<string, unknown>>,
): IManageV2Source[] {
  return sources.map((item, index) => {
    const extra = containers.find((container) => (
      containerIdsMatch(String(container.id ?? ''), item.id)
    )) ?? (containers.length === sources.length ? containers[index] : undefined)
    if (!extra) {
      return item
    }
    return {
      ...item,
      name: String(extra.name || item.name),
      path: String(extra.path || item.path),
      wstype: typeof extra.wstype === 'string' ? extra.wstype : item.wstype,
      workspace_id: typeof extra.workspace_id === 'string' ? extra.workspace_id : item.workspace_id,
      workspace_name: typeof extra.workspace_name === 'string' ? extra.workspace_name : item.workspace_name,
      ancestor_ids: Array.isArray(extra.ancestor_ids) ? extra.ancestor_ids as string[] : item.ancestor_ids,
      preview_children: extra.preview_children as SyncPreviewSample[] | null | undefined,
    }
  })
}

export function imanagePreviewKind(item: IManageV2Source): 'folder' | 'workspace' {
  return item.wstype === 'workspace' ? 'workspace' : 'folder'
}

export function resolvingSourcesLabel(count: number): string {
  return count === 1
    ? 'Loading folder from iManage'
    : `Loading ${count} folders from iManage`
}

export function imanageFileCountLabel(count: number): string {
  const formatted = count.toLocaleString()
  return count === 1 ? `${formatted} file` : `${formatted} files`
}

export function lookupImanageDocumentCount(
  counts: Record<string, number>,
  containerId: string,
): number | undefined {
  if (containerId in counts) {
    return counts[containerId]
  }
  const match = Object.entries(counts).find(([id]) => imanageContainerIdsMatch(id, containerId))
  return match?.[1]
}

export function totalImanageDocumentCount(
  counts: Record<string, number>,
  containerIds: string[],
): number | undefined {
  if (containerIds.length === 0) {
    return undefined
  }
  let total = 0
  for (const id of containerIds) {
    const count = lookupImanageDocumentCount(counts, id)
    if (count === undefined) {
      return undefined
    }
    total += count
  }
  return total
}

export async function fetchImanageDocumentCounts(
  auth: IManageV2Auth,
  ids: string[],
  signal?: AbortSignal,
): Promise<Record<string, number>> {
  const containerIds = ids.filter(Boolean)
  if (containerIds.length === 0 || !auth.providerConfig.access_token || !auth.providerConfig.customer_id) {
    return {}
  }
  try {
    const response = await backendClient.post(
      '/imanage/container-document-counts',
      {
        access_token: auth.providerConfig.access_token,
        customer_id: auth.providerConfig.customer_id,
        user_id: auth.providerConfig.user_id,
        ids: containerIds,
      },
      { signal, timeout: 10_000 },
    )
    const counts: Record<string, number> = {}
    for (const row of response.data?.counts || []) {
      if (
        typeof row?.id === 'string'
        && typeof row?.document_count === 'number'
        && Number.isFinite(row.document_count)
      ) {
        counts[row.id] = row.document_count
      }
    }
    return counts
  } catch {
    return {}
  }
}

export async function authenticateImanage(runOAuthPopup: RunOAuthPopup): Promise<IManageV2Auth> {
  const response = await backendClient.get('/imanage/oauth/authorize')
  const data = response.data
  if (!data.auth_url) {
    throw new Error('Invalid iManage authorization response. Please try again.')
  }
  if (!data.state) {
    throw new Error('Invalid iManage authorization response. Please try again.')
  }

  const authData = await runOAuthPopup<IManageAuthPayload>({
    authUrl: data.auth_url,
    popupName: 'iManageAuth',
    successMessageType: 'imanage-auth-success',
    acknowledgmentMessageType: 'imanage-auth-acknowledged',
    stateStorage: {
      key: 'imanage_oauth_state',
      value: data.state,
    },
    extractAuthData: (eventData) => eventData?.authData || null,
  })

  if (!authData.customer_id) {
    throw new Error('iManage authentication payload is missing customer ID. Please reconnect iManage.')
  }
  if (!authData.imanage_account_id) {
    throw new Error('iManage authentication payload is missing account ID. Please reconnect iManage.')
  }
  if (!authData.access_token) {
    throw new Error('iManage authentication payload is missing access token. Please reconnect iManage.')
  }

  return {
    accessToken: authData.access_token,
    providerConfig: {
      access_token: authData.access_token,
      dialog_token: authData.dialog_token,
      user_id: authData.user_id,
      customer_id: authData.customer_id,
      imanage_account_id: authData.imanage_account_id,
    },
  }
}

export async function resolveImanageSources(
  auth: IManageV2Auth,
  sources: IManageV2Source[],
): Promise<IManageV2Source[]> {
  const containerIds = sources.map((item) => item.id).filter(Boolean)
  if (containerIds.length === 0 || !auth.providerConfig.access_token || !auth.providerConfig.customer_id) {
    return sources
  }

  const response = await backendClient.post('/imanage/resolve-containers', {
    access_token: auth.providerConfig.access_token,
    customer_id: auth.providerConfig.customer_id,
    user_id: auth.providerConfig.user_id,
    ids: containerIds,
  })
  return mergeResolvedContainers(sources, response.data.containers || [])
}

export function imanageV2CreateBody({
  destinationFolderId,
  mappingMode,
  auth,
  sources,
}: {
  destinationFolderId: number | null
  mappingMode: SyncMappingMode
  auth: IManageV2Auth
  sources: IManageV2Source[]
}) {
  return {
    provider_type: 'imanage',
    destination_folder_id: destinationFolderId,
    mapping_mode: mappingMode,
    provider_config: { ...auth.providerConfig },
    folders: sources.map((folder) => ({
      folder_id: folder.id,
      folder_name: folder.name,
      folder_path: folder.path,
      workspace_id: folder.workspace_id || (folder.wstype === 'workspace' ? folder.id : undefined),
      workspace_name: folder.workspace_name,
      sync_mode: folder.wstype === 'workspace' ? 'workspace' : 'folder',
    })),
  }
}

export async function createImanageV2Syncs({
  matterGuid,
  destinationFolderId,
  mappingMode,
  auth,
  sources,
}: {
  matterGuid: string
  destinationFolderId: number | null
  mappingMode: SyncMappingMode
  auth: IManageV2Auth
  sources: IManageV2Source[]
}): Promise<number[]> {
  const response = await backendClient.post(
    `/sync/folders/multi?matter_guid=${matterGuid}`,
    imanageV2CreateBody({ destinationFolderId, mappingMode, auth, sources }),
  )
  return response.data.sync_folder_ids || []
}
